import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AppService } from '../app.service';
import { ApiRequestService } from '../shared/api-request.service';
import { UtilsService } from '../shared/utils.service';

/**
 * HubspotService loads and manages the HubSpot live chat (Conversations) widget.
 *
 * The widget is only ever used when a HubSpot portal id is configured for the
 * active environment (environment.HUBSPOT_PORTAL_ID). When a portal id is not set,
 * every method in this service is a no-op, so nothing HubSpot-related runs.
 *
 * CUSTOM LAUNCHER: the default floating HubSpot bubble is suppressed
 * (`loadImmediately: false`, and the widget UI is never auto-loaded). The chat is
 * opened from our own button in the top navigation (see TopNavComponent) via
 * openChat(). When the user closes the chat, the widget is removed so no bubble
 * lingers over page content (this was blocking pagination controls). The HubSpot
 * script itself is still loaded on startup so page-view tracking keeps working.
 *
 * Identity handling (drives simple chat vs AI chat via HubSpot chatflow targeting):
 *  - Logged-out visitors are anonymous (no identification) -> simple live chat.
 *  - Logged-in users are identified with a signed token from the backend
 *    (v2/hubspot/identification-token) -> known contact -> AI chat. If the token
 *    cannot be obtained, the user stays anonymous and falls back to the simple chat.
 *  - Identity is applied to hsConversationsSettings up front; the widget reads it
 *    when opened. Only `email` is sent under a standard property name; all other
 *    properties are namespaced (siteconnect_*) so standard contact fields are never
 *    overwritten.
 *
 * Lifecycle:
 *  - app.component.ts calls onInit() on startup.
 *  - sign-in.component.ts calls onIdentifyUser() after login / auto token validation.
 *  - auth.service.ts calls onReset() on logout.
 *  - top-nav.component.ts calls openChat() when the user clicks the chat button.
 */
@Injectable({
  providedIn: 'root'
})
export class HubspotService {

  // The id used for the injected HubSpot script tag. HubSpot's own loader uses this id.
  private readonly scriptId: string = 'hs-script-loader';

  // The platform label attached to every HubSpot conversation so the inbox,
  // routing rules, and bot/chatflows know the visitor is using the web portal.
  private readonly platform: string = 'Web Portal';

  // Prefix applied to every non-email property sent to HubSpot. Namespacing keeps
  // our data off HubSpot's standard contact properties (firstname, phone, company,
  // etc.) so we never overwrite existing contact details. Only `email` is sent
  // under a standard property name, purely as the contact match key.
  // NOTE: HubSpot only stores these if matching custom contact properties exist in
  // the portal; unknown properties are ignored (never stored, never an error).
  private readonly propertyPrefix: string = 'siteconnect_';

  // Tracks whether the HubSpot script has been requested to avoid loading it twice.
  private isLoaded: boolean = false;

  // Tracks whether the widget open/close event handlers have been registered.
  private handlersRegistered: boolean = false;

  // Set when the user requested to open the chat but the widget was not loaded yet.
  private pendingOpen: boolean = false;

  // Resolvers for openChat() promises awaiting the widget to finish loading/opening.
  private openResolvers: Array<() => void> = [];

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    private utils: UtilsService
  ) { }

  /**
   * Whether the HubSpot live chat is configured for this environment. Used to
   * show/hide the chat button in the top navigation.
   *
   * @return {boolean} True when a portal id is set.
   */
  isEnabled(): boolean {
    return !!environment.HUBSPOT_PORTAL_ID;
  }

  /**
   * Called once on application start (see app.component.ts). Loads the HubSpot
   * script (for page-view tracking) without showing the default bubble, and
   * prepares the visitor's identity so the chat is correct when opened.
   *
   * @return {void} Does not return a value.
   */
  onInit(): void {
    if ( !this.isEnabled() ) {
      return;
    }
    this.prepareWidget();
    if ( this.app.isAuthenticated && this.app.user?.email ) {
      this.identifyCurrentUser();
    } else {
      this.setAnonymous();
    }
  }

  /**
   * Opens the chat window. Loads the widget on demand if it is not already loaded.
   * This is the custom launcher entry point, triggered from the top navigation.
   * The returned promise resolves once the widget has been opened, so the caller
   * can show a loading indicator while the widget loads.
   *
   * @return {Promise<void>} Resolves when the widget is opened (or immediately if disabled).
   */
  openChat(): Promise<void> {
    if ( !this.isEnabled() ) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve): void => {
      this.prepareWidget();
      this.whenWidgetReady((): void => {
        const conversations = window.HubSpotConversations;
        if ( !conversations?.widget ) {
          resolve();
          return;
        }
        const status = conversations.widget.status ? conversations.widget.status() : { loaded: false, pending: false };
        if ( status?.loaded ) {
          conversations.widget.open();
          resolve();
        } else {
          // Load the widget, then open it (and resolve) once widgetLoaded fires.
          this.pendingOpen = true;
          this.openResolvers.push(resolve);
          conversations.widget.load();
        }
      });
    });
  }

  /**
   * Identifies the currently authenticated user to HubSpot using the data held in
   * AppService.
   *
   * @return {void} Does not return a value.
   */
  identifyCurrentUser(): void {
    if ( !this.isEnabled() || !this.app.user?.email ) {
      return;
    }
    this.onIdentifyUser(this.app.user.email, {
      firstname: this.app.user.contact_person,
      phone: this.app.user.mobile,
      company: this.app.account?.name,
      trade: this.app.user.trade,
      city: this.app.user.city,
      country: this.app.user.country
    });
  }

  /**
   * Identifies a logged-in user to HubSpot. Queues a namespaced tracking identify
   * call (so nothing overwrites standard contact fields), then applies secure
   * identification so an open (or subsequently opened) chat is routed to the AI chat.
   *
   * @param {string} email - The visitor's email address (the contact match key).
   * @param {Record<string, unknown>} properties - Additional properties (namespaced before sending).
   * @return {void} Does not return a value.
   */
  onIdentifyUser(email: string, properties: Record<string, unknown> = {}): void {
    if ( !this.isEnabled() || !email ) {
      return;
    }

    // Queue the tracking identify call. Only `email` is sent under a standard
    // property name (the contact match key); every other property is namespaced
    // so we never overwrite HubSpot's standard contact fields.
    const namespacedProperties = this.namespaceProperties({ ...this.getContextProperties(), ...properties });
    window._hsq = window._hsq || [];
    window._hsq.push(['identify', { email, ...namespacedProperties }]);
    window._hsq.push(['trackPageView']);

    this.prepareWidget();
    this.applySecureIdentification(email);
  }

  /**
   * Clears the HubSpot chat state on logout: removes secure identification so the
   * visitor becomes anonymous again, and removes the widget if it is currently open.
   *
   * @return {void} Does not return a value.
   */
  onReset(): void {
    if ( !this.isEnabled() ) {
      return;
    }
    this.setAnonymous();
    // If a chat is currently open, remove it so the logged-out user does not keep
    // an identified session on screen.
    const widget = window.HubSpotConversations?.widget;
    if ( widget && widget.status && widget.status().loaded ) {
      widget.remove();
    }
  }

  /**
   * Builds the base context properties (raw, unprefixed keys) attached to the
   * tracking identify call.
   *
   * @return {Record<string, unknown>} The base context properties.
   */
  private getContextProperties(): Record<string, unknown> {
    return {
      platform: this.platform,
      app_name: environment.APP_NAME,
      app_version: this.utils.getAppVersion(),
      environment: this.utils.getEnvModeAsText(),
      is_production: this.utils.isProduction()
    };
  }

  /**
   * Applies the namespace prefix to every property key so nothing collides with
   * HubSpot's standard contact properties (e.g. `firstname` -> `siteconnect_firstname`).
   *
   * @param {Record<string, unknown>} properties - The raw (unprefixed) properties.
   * @return {Record<string, unknown>} The properties with prefixed keys.
   */
  private namespaceProperties(properties: Record<string, unknown>): Record<string, unknown> {
    const namespaced: Record<string, unknown> = {};
    Object.keys(properties).forEach((key: string): void => {
      namespaced[`${this.propertyPrefix}${key}`] = properties[key];
    });
    return namespaced;
  }

  /**
   * Loads the HubSpot script (once) with the default bubble suppressed, and
   * registers the widget open/close event handlers. Does NOT display the widget.
   *
   * @return {void} Does not return a value.
   */
  private prepareWidget(): void {
    // Never let the widget auto-load; we control opening via the custom launcher.
    window.hsConversationsSettings = {
      ...(window.hsConversationsSettings || {}),
      loadImmediately: false
    };

    if ( this.isLoaded ) {
      this.registerHandlers();
      return;
    }

    window._hsq = window._hsq || [];
    this.utils.loadExternalScript(this.scriptId, `//js.hs-scripts.com/${environment.HUBSPOT_PORTAL_ID}.js`, (): void => {
      this.isLoaded = true;
      this.registerHandlers();
    });
  }

  /**
   * Registers the widgetLoaded/widgetClosed handlers once the Conversations API is
   * ready. widgetLoaded opens the widget when an open was requested; widgetClosed
   * removes the widget so no launcher bubble lingers over the page.
   *
   * @return {void} Does not return a value.
   */
  private registerHandlers(): void {
    if ( this.handlersRegistered ) {
      return;
    }
    this.handlersRegistered = true;
    this.whenWidgetReady((): void => {
      const conversations = window.HubSpotConversations;
      if ( !conversations?.on ) {
        return;
      }
      conversations.on('widgetLoaded', (): void => {
        if ( this.pendingOpen ) {
          this.pendingOpen = false;
          window.HubSpotConversations?.widget?.open();
        }
        // Resolve any openChat() promises now that the widget has loaded/opened.
        const resolvers = this.openResolvers;
        this.openResolvers = [];
        resolvers.forEach((resolve): void => resolve());
      });
      conversations.on('widgetClosed', (): void => {
        // Remove the widget when closed so the floating bubble does not remain.
        const widget = window.HubSpotConversations?.widget;
        if ( widget && widget.status && widget.status().loaded ) {
          widget.remove();
        }
      });
    });
  }

  /**
   * Fetches a signed visitor identification token from the backend and applies it
   * to hsConversationsSettings so a known contact is routed to the AI chat. On any
   * failure or a missing token, identification is cleared so the visitor falls back
   * to the simple live chat.
   *
   * @param {string} email - The visitor's email address.
   * @return {void} Does not return a value.
   */
  private applySecureIdentification(email: string): void {
    this.api.makeRequest('get', 'v2/hubspot/identification-token', {}, {}, { 'X-Skip-Spinner': 'true' })
      .then((response: any): void => {
        const token: string | null = response?.data?.token ?? null;
        if ( token ) {
          window.hsConversationsSettings = {
            ...(window.hsConversationsSettings || {}),
            loadImmediately: false,
            identificationEmail: email,
            identificationToken: token
          };
        } else {
          console.warn('[HubSpot] No identification token returned; using the simple live chat.');
          this.clearIdentification();
        }
        this.refreshIfOpen();
      })
      .catch((error: any): void => {
        console.warn('[HubSpot] Identification token request failed; using the simple live chat.', error);
        this.clearIdentification();
        this.refreshIfOpen();
      });
  }

  /**
   * Marks the visitor as anonymous by removing any secure identification.
   *
   * @return {void} Does not return a value.
   */
  private setAnonymous(): void {
    this.clearIdentification();
    this.refreshIfOpen();
  }

  /**
   * Removes any secure identification settings so the visitor is treated as
   * anonymous (simple live chat) on the next widget load.
   *
   * @return {void} Does not return a value.
   */
  private clearIdentification(): void {
    if ( window.hsConversationsSettings ) {
      delete window.hsConversationsSettings.identificationEmail;
      delete window.hsConversationsSettings.identificationToken;
    }
  }

  /**
   * If the chat is currently open, remove it so it reloads with the new identity
   * the next time it is opened. If it is not open, nothing needs to happen (the
   * updated settings are read on the next open).
   *
   * @return {void} Does not return a value.
   */
  private refreshIfOpen(): void {
    const widget = window.HubSpotConversations?.widget;
    if ( widget && widget.status && widget.status().loaded ) {
      widget.remove();
    }
  }

  /**
   * Runs a callback once the HubSpot Conversations API is available. If it is
   * already available the callback runs immediately, otherwise it is queued via
   * hsConversationsOnReady.
   *
   * @param {() => void} callback - The callback to run when the API is ready.
   * @return {void} Does not return a value.
   */
  private whenWidgetReady(callback: () => void): void {
    if ( window.HubSpotConversations?.widget ) {
      callback();
    } else {
      window.hsConversationsOnReady = window.hsConversationsOnReady || [];
      window.hsConversationsOnReady.push(callback);
    }
  }
}
